// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsTabSection} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {
	COMMUNITY_MEMBERS_DESCRIPTOR,
	FRIENDS_OF_FRIENDS_DESCRIPTOR,
} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import * as UserSettingsCommands from '@app/features/user/commands/UserSettingsCommands';
import styles from '@app/features/user/components/modals/tabs/privacy_safety_tab/CommunicationTab.module.css';
import UserSettings from '@app/features/user/state/UserSettings';
import {GroupDmAddPermissionFlags, IncomingCallFlags} from '@fluxer/constants/src/UserConstants';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';

const INCOMING_CALL_PERMISSIONS_LABEL_DESCRIPTOR = msg({
	message: 'Incoming call permissions',
	comment: 'Privacy & safety > Communication: aria-label for the radio group controlling who can call the user.',
});
const NOBODY_OPTION_DESCRIPTOR = msg({
	message: 'Nobody',
	comment:
		'Privacy & safety > Communication: radio option label meaning no one is allowed (for calls or group DM adds).',
});
const INCOMING_CALL_NOBODY_OPTION_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Block every incoming call',
	comment: 'Privacy & safety > Communication: helper text under the Nobody radio option for incoming calls.',
});
const FRIENDS_ONLY_OPTION_DESCRIPTOR = msg({
	message: 'Friends only',
	comment: 'Privacy & safety > Communication: radio option label restricting calls or group DM adds to friends only.',
});
const INCOMING_CALL_FRIENDS_ONLY_OPTION_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Only friends can call (recommended)',
	comment: 'Privacy & safety > Communication: helper text under the Friends only option for incoming calls.',
});
const FRIENDS_PLUS_CUSTOM_OPTION_DESCRIPTOR = msg({
	message: 'Friends + custom',
	comment:
		'Privacy & safety > Communication: radio option label for the custom permission tier above Friends only. The + is literal.',
});
const INCOMING_CALL_CUSTOM_OPTION_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Friends, plus groups you pick',
	comment: 'Privacy & safety > Communication: helper text under the Friends + custom option for incoming calls.',
});
const EVERYONE_OPTION_DESCRIPTOR = msg({
	message: 'Everyone',
	comment: 'Privacy & safety > Communication: radio option label allowing anyone to call or add the user.',
});
const INCOMING_CALL_EVERYONE_OPTION_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Anyone can call, even strangers',
	comment: 'Privacy & safety > Communication: helper text under the Everyone option for incoming calls.',
});
const GROUP_DM_ADD_PERMISSIONS_LABEL_DESCRIPTOR = msg({
	message: 'Group chat add permissions',
	comment:
		'Privacy & safety > Communication: aria-label for the radio group controlling who can add the user to group DMs.',
});
const GROUP_DM_ADD_NOBODY_OPTION_DESCRIPTION_DESCRIPTOR = msg({
	message: "Don't let anyone add you to group chats without asking",
	comment: 'Privacy & safety > Communication: helper text under the Nobody option for group DM adds.',
});
const GROUP_DM_ADD_FRIENDS_ONLY_OPTION_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Only friends can add you without asking (recommended)',
	comment: 'Privacy & safety > Communication: helper text under the Friends only option for group DM adds.',
});
const GROUP_DM_ADD_CUSTOM_OPTION_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Friends, plus groups you pick',
	comment: 'Privacy & safety > Communication: helper text under the Friends + custom option for group DM adds.',
});
const GROUP_DM_ADD_EVERYONE_OPTION_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Anyone can add you to group chats',
	comment: 'Privacy & safety > Communication: helper text under the Everyone option for group DM adds.',
});
export const CommunicationTabContent: React.FC = observer(() => {
	const {i18n} = useLingui();
	const incomingCallFlags = UserSettings.getIncomingCallFlags();
	const groupDmAddPermissionFlags = UserSettings.getGroupDmAddPermissionFlags();
	const hasCallFlag = (flag: number) => (incomingCallFlags & flag) === flag;
	const hasGroupDmAddFlag = (flag: number) => (groupDmAddPermissionFlags & flag) === flag;
	const getIncomingCallBaseValue = (): 'nobody' | 'friends_only' | 'everyone' | 'custom' => {
		if (hasCallFlag(IncomingCallFlags.NOBODY)) return 'nobody';
		if (hasCallFlag(IncomingCallFlags.EVERYONE)) return 'everyone';
		if (hasCallFlag(IncomingCallFlags.FRIENDS_ONLY)) return 'friends_only';
		return 'custom';
	};
	const handleIncomingCallBaseChange = async (value: 'nobody' | 'friends_only' | 'everyone' | 'custom') => {
		let newFlags = 0;
		if (value === 'nobody') {
			newFlags = IncomingCallFlags.NOBODY;
		} else if (value === 'friends_only') {
			newFlags = IncomingCallFlags.FRIENDS_ONLY;
		} else if (value === 'everyone') {
			newFlags = IncomingCallFlags.EVERYONE;
		} else {
			newFlags = incomingCallFlags & (IncomingCallFlags.FRIENDS_OF_FRIENDS | IncomingCallFlags.GUILD_MEMBERS);
			if (newFlags === 0) {
				newFlags = IncomingCallFlags.FRIENDS_OF_FRIENDS;
			}
		}
		await UserSettingsCommands.update({incomingCallFlags: newFlags});
	};
	const handleIncomingCallAdditiveToggle = async (flag: number, value: boolean) => {
		let newFlags = incomingCallFlags;
		newFlags &= ~IncomingCallFlags.NOBODY;
		newFlags &= ~IncomingCallFlags.FRIENDS_ONLY;
		newFlags &= ~IncomingCallFlags.EVERYONE;
		if (value) {
			newFlags |= flag;
		} else {
			newFlags &= ~flag;
		}
		if (newFlags === 0) {
			newFlags = IncomingCallFlags.FRIENDS_ONLY;
		}
		await UserSettingsCommands.update({incomingCallFlags: newFlags});
	};
	const handleIncomingCallModifierToggle = async (flag: number, value: boolean) => {
		let newFlags = incomingCallFlags;
		if (value) {
			newFlags |= flag;
		} else {
			newFlags &= ~flag;
		}
		await UserSettingsCommands.update({incomingCallFlags: newFlags});
	};
	const getGroupDmAddBaseValue = (): 'nobody' | 'friends_only' | 'everyone' | 'custom' => {
		if (hasGroupDmAddFlag(GroupDmAddPermissionFlags.NOBODY)) return 'nobody';
		if (hasGroupDmAddFlag(GroupDmAddPermissionFlags.EVERYONE)) return 'everyone';
		if (hasGroupDmAddFlag(GroupDmAddPermissionFlags.FRIENDS_ONLY)) return 'friends_only';
		return 'custom';
	};
	const handleGroupDmAddBaseChange = async (value: 'nobody' | 'friends_only' | 'everyone' | 'custom') => {
		let newFlags = 0;
		if (value === 'nobody') {
			newFlags = GroupDmAddPermissionFlags.NOBODY;
		} else if (value === 'friends_only') {
			newFlags = GroupDmAddPermissionFlags.FRIENDS_ONLY;
		} else if (value === 'everyone') {
			newFlags = GroupDmAddPermissionFlags.EVERYONE;
		} else {
			newFlags =
				groupDmAddPermissionFlags &
				(GroupDmAddPermissionFlags.FRIENDS_OF_FRIENDS | GroupDmAddPermissionFlags.GUILD_MEMBERS);
			if (newFlags === 0) {
				newFlags = GroupDmAddPermissionFlags.FRIENDS_OF_FRIENDS;
			}
		}
		await UserSettingsCommands.update({groupDmAddPermissionFlags: newFlags});
	};
	const handleGroupDmAddAdditiveToggle = async (flag: number, value: boolean) => {
		let newFlags = groupDmAddPermissionFlags;
		newFlags &= ~GroupDmAddPermissionFlags.NOBODY;
		newFlags &= ~GroupDmAddPermissionFlags.FRIENDS_ONLY;
		newFlags &= ~GroupDmAddPermissionFlags.EVERYONE;
		if (value) {
			newFlags |= flag;
		} else {
			newFlags &= ~flag;
		}
		if (newFlags === 0) {
			newFlags = GroupDmAddPermissionFlags.FRIENDS_ONLY;
		}
		await UserSettingsCommands.update({groupDmAddPermissionFlags: newFlags});
	};
	return (
		<>
			<SettingsTabSection
				title={<Trans>Incoming calls</Trans>}
				description={<Trans>Control who can call you</Trans>}
				data-flx="user.privacy-safety-tab.communication-tab.communication-tab-content.settings-tab-section"
			>
				<RadioGroup
					value={getIncomingCallBaseValue()}
					onChange={handleIncomingCallBaseChange}
					aria-label={i18n._(INCOMING_CALL_PERMISSIONS_LABEL_DESCRIPTOR)}
					options={[
						{
							value: 'nobody',
							name: i18n._(NOBODY_OPTION_DESCRIPTOR),
							desc: i18n._(INCOMING_CALL_NOBODY_OPTION_DESCRIPTION_DESCRIPTOR),
						},
						{
							value: 'friends_only',
							name: i18n._(FRIENDS_ONLY_OPTION_DESCRIPTOR),
							desc: i18n._(INCOMING_CALL_FRIENDS_ONLY_OPTION_DESCRIPTION_DESCRIPTOR),
						},
						{
							value: 'custom',
							name: i18n._(FRIENDS_PLUS_CUSTOM_OPTION_DESCRIPTOR),
							desc: i18n._(INCOMING_CALL_CUSTOM_OPTION_DESCRIPTION_DESCRIPTOR),
						},
						{
							value: 'everyone',
							name: i18n._(EVERYONE_OPTION_DESCRIPTOR),
							desc: i18n._(INCOMING_CALL_EVERYONE_OPTION_DESCRIPTION_DESCRIPTOR),
						},
					]}
					data-flx="user.privacy-safety-tab.communication-tab.communication-tab-content.radio-group.incoming-call-base-change"
				/>
				{getIncomingCallBaseValue() === 'custom' && (
					<>
						<div
							className={styles.subsectionHeader}
							data-flx="user.privacy-safety-tab.communication-tab.communication-tab-content.subsection-header"
						>
							<Trans>Additional groups</Trans>
						</div>
						<Switch
							label={i18n._(FRIENDS_OF_FRIENDS_DESCRIPTOR)}
							description={<Trans>People who are friends with your friends can call you</Trans>}
							value={hasCallFlag(IncomingCallFlags.FRIENDS_OF_FRIENDS)}
							onChange={(value) => handleIncomingCallAdditiveToggle(IncomingCallFlags.FRIENDS_OF_FRIENDS, value)}
							data-flx="user.privacy-safety-tab.communication-tab.communication-tab-content.switch.incoming-call-additive-toggle"
						/>
						<Switch
							label={i18n._(COMMUNITY_MEMBERS_DESCRIPTOR)}
							description={<Trans>People from communities you're both in can call you</Trans>}
							value={hasCallFlag(IncomingCallFlags.GUILD_MEMBERS)}
							onChange={(value) => handleIncomingCallAdditiveToggle(IncomingCallFlags.GUILD_MEMBERS, value)}
							data-flx="user.privacy-safety-tab.communication-tab.communication-tab-content.switch.incoming-call-additive-toggle--2"
						/>
					</>
				)}
				{getIncomingCallBaseValue() !== 'nobody' && (
					<>
						<div
							className={styles.subsectionHeader}
							data-flx="user.privacy-safety-tab.communication-tab.communication-tab-content.subsection-header--2"
						>
							<Trans>Ring behavior</Trans>
						</div>
						<Switch
							label={<Trans>Silent calls from everyone</Trans>}
							description={
								<Trans>
									All calls will notify silently instead of ringing. By default, calls from non-friends are always
									silent.
								</Trans>
							}
							value={hasCallFlag(IncomingCallFlags.SILENT_EVERYONE)}
							onChange={(value) => handleIncomingCallModifierToggle(IncomingCallFlags.SILENT_EVERYONE, value)}
							data-flx="user.privacy-safety-tab.communication-tab.communication-tab-content.switch.incoming-call-modifier-toggle"
						/>
					</>
				)}
			</SettingsTabSection>
			<SettingsTabSection
				title={<Trans>Who can add you to group chats</Trans>}
				description={
					<Trans>
						Control who can add you to group chats without asking. Anyone can still send you invite links to join.
					</Trans>
				}
				data-flx="user.privacy-safety-tab.communication-tab.communication-tab-content.settings-tab-section--2"
			>
				<RadioGroup
					value={getGroupDmAddBaseValue()}
					onChange={handleGroupDmAddBaseChange}
					aria-label={i18n._(GROUP_DM_ADD_PERMISSIONS_LABEL_DESCRIPTOR)}
					options={[
						{
							value: 'nobody',
							name: i18n._(NOBODY_OPTION_DESCRIPTOR),
							desc: i18n._(GROUP_DM_ADD_NOBODY_OPTION_DESCRIPTION_DESCRIPTOR),
						},
						{
							value: 'friends_only',
							name: i18n._(FRIENDS_ONLY_OPTION_DESCRIPTOR),
							desc: i18n._(GROUP_DM_ADD_FRIENDS_ONLY_OPTION_DESCRIPTION_DESCRIPTOR),
						},
						{
							value: 'custom',
							name: i18n._(FRIENDS_PLUS_CUSTOM_OPTION_DESCRIPTOR),
							desc: i18n._(GROUP_DM_ADD_CUSTOM_OPTION_DESCRIPTION_DESCRIPTOR),
						},
						{
							value: 'everyone',
							name: i18n._(EVERYONE_OPTION_DESCRIPTOR),
							desc: i18n._(GROUP_DM_ADD_EVERYONE_OPTION_DESCRIPTION_DESCRIPTOR),
						},
					]}
					data-flx="user.privacy-safety-tab.communication-tab.communication-tab-content.radio-group.group-dm-add-base-change"
				/>
				{getGroupDmAddBaseValue() === 'custom' && (
					<>
						<div
							className={styles.subsectionHeader}
							data-flx="user.privacy-safety-tab.communication-tab.communication-tab-content.subsection-header--3"
						>
							<Trans>Additional groups</Trans>
						</div>
						<Switch
							label={i18n._(FRIENDS_OF_FRIENDS_DESCRIPTOR)}
							description={<Trans>People who are friends with your friends can add you to group chats</Trans>}
							value={hasGroupDmAddFlag(GroupDmAddPermissionFlags.FRIENDS_OF_FRIENDS)}
							onChange={(value) => handleGroupDmAddAdditiveToggle(GroupDmAddPermissionFlags.FRIENDS_OF_FRIENDS, value)}
							data-flx="user.privacy-safety-tab.communication-tab.communication-tab-content.switch.group-dm-add-additive-toggle"
						/>
						<Switch
							label={i18n._(COMMUNITY_MEMBERS_DESCRIPTOR)}
							description={<Trans>People from communities you're both in can add you to group chats</Trans>}
							value={hasGroupDmAddFlag(GroupDmAddPermissionFlags.GUILD_MEMBERS)}
							onChange={(value) => handleGroupDmAddAdditiveToggle(GroupDmAddPermissionFlags.GUILD_MEMBERS, value)}
							data-flx="user.privacy-safety-tab.communication-tab.communication-tab-content.switch.group-dm-add-additive-toggle--2"
						/>
					</>
				)}
			</SettingsTabSection>
		</>
	);
});
